import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const initialCustomerState = {
  company_name: '',
  address: '',
  pancard: '',
  city: '',
  state: '',
  country: '',
  gst_number: '',
  gst_uin: '',
  code: '',
  cin: '',
  email: '',
};

type CustomerType = typeof initialCustomerState & { id?: string | number };

const AddCustomer: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const customerId = searchParams.get('customer_id');
  const isEditMode = customerId && customerId !== 'new';

  const [customer, setCustomer] = useState<CustomerType>(initialCustomerState);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [formError, setFormError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch existing customer if in edit mode
  useEffect(() => {
    if (isEditMode && customerId) {
      setLoading(true);
      axios
        .get(`${API_URL}/auth/customer/${customerId}`)
        .then((res) => setCustomer(res.data))
        .catch(() => setError('Unable to load customer details.'))
        .finally(() => setLoading(false));
    } else {
      setCustomer(initialCustomerState);
    }
  }, [customerId, isEditMode]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setCustomer((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setSuccess('');
    if (!customer.company_name.trim()) {
      setFormError('Company name is required.');
      return;
    }

    setSubmitting(true);
    try {
      if (isEditMode && customerId) {
        // PUT (update)
        await axios.put(`${API_URL}/auth/customer/${customerId}`, customer);
        setSuccess('Customer updated successfully');
      } else {
        // POST (create)
        const { data } = await axios.post(`${API_URL}/auth/customer`, customer);
        setSuccess('Customer added successfully');
        // Optionally redirect to edit page after creation
        navigate(`/customer`);
      }
    } catch (e: any) {
      setFormError(e?.response?.data?.error || 'Error saving customer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    setError('');
    try {
      await axios.delete(`${API_URL}/auth/customer/${customerId}`);
      setShowDeleteConfirm(false);
      setSuccess('Customer deleted successfully.');
      setTimeout(() => navigate('/customers'), 1200);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Error deleting customer');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto p-6 bg-white rounded shadow">
      <h2 className="text-lg font-semibold mb-4">
        {isEditMode ? 'Edit Customer' : 'Add Customer'}
      </h2>
      {error && <div className="text-red-600 mb-3">{error}</div>}
      {formError && <div className="text-red-600 mb-3">{formError}</div>}
      {success && <div className="text-green-600 mb-3">{success}</div>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium">
            Company Name <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            name="company_name"
            className="w-full border px-3 py-2 rounded"
            value={customer.company_name}
            onChange={handleInputChange}
            required
            disabled={loading || submitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium">Address</label>
          <textarea
            name="address"
            className="w-full border px-3 py-2 rounded"
            value={customer.address}
            onChange={handleInputChange}
            rows={2}
            disabled={loading || submitting}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">PAN</label>
            <input
              type="text"
              name="pancard"
              className="w-full border px-3 py-2 rounded"
              value={customer.pancard}
              onChange={handleInputChange}
              disabled={loading || submitting}
            />
          </div>
          <div>
            <label className="block text-sm font-medium">City</label>
            <input
              type="text"
              name="city"
              className="w-full border px-3 py-2 rounded"
              value={customer.city}
              onChange={handleInputChange}
              disabled={loading || submitting}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">State</label>
            <input
              type="text"
              name="state"
              className="w-full border px-3 py-2 rounded"
              value={customer.state}
              onChange={handleInputChange}
              disabled={loading || submitting}
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Country</label>
            <input
              type="text"
              name="country"
              className="w-full border px-3 py-2 rounded"
              value={customer.country}
              onChange={handleInputChange}
              disabled={loading || submitting}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">GST Number</label>
            <input
              type="text"
              name="gst_number"
              className="w-full border px-3 py-2 rounded"
              value={customer.gst_number}
              onChange={handleInputChange}
              disabled={loading || submitting}
            />
          </div>
          <div>
            <label className="block text-sm font-medium">GST UIN</label>
            <input
              type="text"
              name="gst_uin"
              className="w-full border px-3 py-2 rounded"
              value={customer.gst_uin}
              onChange={handleInputChange}
              disabled={loading || submitting}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">Code</label>
            <input
              type="text"
              name="code"
              className="w-full border px-3 py-2 rounded"
              value={customer.code}
              onChange={handleInputChange}
              disabled={loading || submitting}
            />
          </div>
          <div>
            <label className="block text-sm font-medium">CIN</label>
            <input
              type="text"
              name="cin"
              className="w-full border px-3 py-2 rounded"
              value={customer.cin}
              onChange={handleInputChange}
              disabled={loading || submitting}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium">Email</label>
          <input
            type="email"
            name="email"
            className="w-full border px-3 py-2 rounded"
            value={customer.email}
            onChange={handleInputChange}
            disabled={loading || submitting}
          />
        </div>

        <div className="flex space-x-3 mt-6">
          <button
            type="submit"
            className={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition ${submitting ? 'opacity-60 cursor-not-allowed' : ''
              }`}
            disabled={submitting || loading}
          >
            {submitting
              ? isEditMode
                ? 'Saving...'
                : 'Adding...'
              : isEditMode
                ? 'Save Changes'
                : 'Add Customer'}
          </button>
          {isEditMode && (
            <>
              <button
                type="button"
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleteLoading}
              >
                Delete
              </button>
              {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-40">
                  <div className="bg-white rounded shadow-lg p-6 w-[350px] space-y-4">
                    <div className="font-semibold text-lg">Delete Customer</div>
                    <div>Are you sure you want to delete this customer? This will also delete all related bank details.</div>
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => setShowDeleteConfirm(false)}
                        className="px-3 py-1 rounded border"
                        disabled={deleteLoading}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDelete}
                        className={`px-3 py-1 rounded bg-red-600 text-white ${deleteLoading ? 'opacity-60' : ''}`}
                        disabled={deleteLoading}
                      >
                        {deleteLoading ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default AddCustomer;